import { useEffect, useMemo, useRef, useState } from "react";
import { alternateStreamUrls } from "../library/streamAlternates.js";
import { useNavigate } from "react-router";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Channel, SeriesItem, VodItem } from "@hybrid-iptv/types";
import { api } from "../api/client.js";
import { providerBody, useActiveProviderSourceId } from "../epg/api.js";
import { GUIDE_FIRST_HOURS, programIndexFor, useChannels } from "../epg/hooks.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { useHiddenChannelIds } from "../library/useFavorites.js";
import { isResumable, useProgressStore } from "../library/progressStore.js";
import type { CatalogKind, CatalogPage, CatalogQuery } from "../library/catalogQuery.js";
import { normalize } from "../search/useUniversalSearch.js";
import { genreLabel, orderMovieGenres } from "../library/genreOrder.js";
import { viewerCountry } from "../settings/countryStore.js";
import { usePlayerStore } from "../player/playerStore.js";
import { MAX_MULTIVIEW_TILES, useMultiviewStore } from "../player/multiviewStore.js";
import { activeProfile, profileSwitchNeedsPin, useProfiles } from "../settings/profileStore.js";
import { isCategoryLocked, useParental, type ParentalFilterState } from "../settings/parentalStore.js";
import { dvr, dvrAvailable } from "../dvr/dvr.js";
import { runRemoteAction } from "./remoteActions.js";
import { noteInput } from "../player/sleepTimer.js";
import type { RemoteActionId } from "./remoteMap.js";
import { showRemoteNote } from "./remoteNote.js";
import { onPhoneRemote, phoneRemote, phoneRemoteAvailable, type PhoneRequest } from "./phoneRemote.js";
import { phoneCategories, phoneChannel, phoneChannelPage, phoneChannelSearch, type NowPlaying } from "./phoneRequests.js";

/**
 * The phone remote's page side (roadmap Section 25).
 *
 * Always: a phone's remote actions (CH+, Guide, Play/pause…) run through the
 * app's own remote-action handlers, the same code the TV remote's mapped
 * buttons and the on-screen buttons reach — never a second copy of it.
 *
 * Only while a phone is connected: the second screen. What is playing goes to
 * the phones as it changes, and their questions (channels, search, favourites,
 * recordings, profiles) are answered from what this profile may see — the same
 * parental-filtered channels as Live TV and catalog queries with the locked
 * categories left out — and "play" does what the matching screen's button does.
 */
export function PhoneRemoteBridge() {
  const [connected, setConnected] = useState(0);

  useEffect(() => {
    if (!phoneRemoteAvailable()) return;
    void phoneRemote.status().then((status) => setConnected(status?.connected ?? 0)).catch(() => undefined);
    const stopPhones = onPhoneRemote("phones", (status) => {
      setConnected(status.connected);
      if (status.joined) showRemoteNote(`📱 ${status.joined} connected`);
    });
    const stopActions = onPhoneRemote("action", ({ id }) => {
      noteInput(); // a phone button is someone being here (Still watching?)
      runRemoteAction(id as RemoteActionId);
    });
    return () => {
      stopPhones();
      stopActions();
    };
  }, []);

  return connected > 0 ? <SecondScreen /> : null;
}

/** What is on, as the phone's "now playing" card shows it. */
function playingState(channels: readonly Channel[], nowPlaying: NowPlaying): Record<string, unknown> {
  const { source, status, currentTime, duration, recentChannels } = usePlayerStore.getState();
  if (!source) return { title: null };
  if (source.type === "live") {
    const channel = recentChannels[0] && recentChannels[0].name === source.title ? channels.find((c) => c.id === recentChannels[0]!.id) : undefined;
    const now = channel ? phoneChannel(channel, nowPlaying).now : null;
    const progress = now ? (Date.now() - now.startMs) / Math.max(1, now.endMs - now.startMs) : 0;
    return { kind: "live", title: source.title, subtitle: now?.title ?? "", art: channel?.logoUrl ?? source.artworkUrl ?? null, status, progress };
  }
  return {
    kind: "vod",
    title: source.title,
    subtitle: status === "paused" ? "Paused" : "",
    art: source.artworkUrl ?? null,
    status,
    progress: duration > 0 ? currentTime / duration : 0,
  };
}

function SecondScreen() {
  const navigate = useNavigate();
  const sourceId = useActiveProviderSourceId();
  const channelsQuery = useChannels(sourceId);
  const hiddenIds = useHiddenChannelIds();
  const channels = useMemo(
    () => (channelsQuery.data?.channels ?? []).filter((channel) => !hiddenIds.has(channel.id)),
    [channelsQuery.data, hiddenIds],
  );
  const queryClient = useQueryClient();

  const live = useRef({ channels, sourceId, navigate, queryClient });
  live.current = { channels, sourceId, navigate, queryClient };

  // What is playing: at once when it changes, and every few seconds for the progress bar.
  useEffect(() => {
    let last = "";
    const publish = async (force: boolean) => {
      const context = live.current;
      const state = await withNowPlaying(context, (nowPlaying) => playingState(context.channels, nowPlaying));
      const key = JSON.stringify([state.title, state.status, state.subtitle]);
      if (!force && key === last) return;
      last = key;
      void phoneRemote.publishState(state).catch(() => undefined);
    };
    void publish(true);
    const stop = usePlayerStore.subscribe(() => void publish(false));
    const timer = window.setInterval(() => void publish(true), 5000);
    return () => {
      stop();
      window.clearInterval(timer);
    };
  }, []);

  useEffect(
    () =>
      onPhoneRemote("request", (request) => {
        noteInput();
        void answer(request, live.current)
          .catch(() => ({ ok: false, message: "The TV could not do that just now." }))
          .then((data) => phoneRemote.reply(request.session, request.id, data))
          .catch(() => undefined);
      }),
    [],
  );

  return null;
}

interface Context {
  channels: Channel[];
  sourceId: string;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: QueryClient;
}

/**
 * Builds an answer with what is on now for the channels it shows. The guide is
 * asked for those channels only (Phase 3 step 2a), so the answer is built once
 * to learn which they are, then again with their programmes. The same four
 * hours Home loads.
 */
async function withNowPlaying<T>(context: Context, build: (nowPlaying: NowPlaying) => T): Promise<T> {
  const shown = new Set<string>();
  const first = build((id) => {
    shown.add(id);
    return null;
  });
  if (shown.size === 0) return first;
  const wanted = context.channels.filter((channel) => shown.has(channel.id));
  const index = await programIndexFor(context.queryClient, context.sourceId, context.channels, wanted, GUIDE_FIRST_HOURS).catch(
    () => null,
  );
  return index ? build((id) => index.getNowPlaying(id)) : first;
}

/** The parental state now, as useParentalFilter would give it. */
function parentalNow(): ParentalFilterState {
  const { pinHash, autoLockAdult, lockedCategoryIds, unlockedUntil } = useParental.getState();
  return { pinHash, autoLockAdult, lockedCategoryIds, unlockedUntil, kidsMode: activeProfile().isKids };
}

/** A kind's categories with their counts, the locked ones left out (useCatalogGenres' rule). */
async function openCategories(sourceId: string, kind: CatalogKind): Promise<Array<[string, number]>> {
  const categories = await api.post<Array<[string, number]>>(`/api/sources/${sourceId}/vod/categories`, { ...providerBody(), kind });
  const parental = parentalNow();
  return (categories ?? []).filter(([id]) => !isCategoryLocked(id, parental));
}

/** Catalog query on the device, the locked categories left out (useCatalogQuery's rule). */
async function catalogPage<T extends VodItem | SeriesItem>(sourceId: string, query: CatalogQuery): Promise<CatalogPage<T>> {
  const categories = await api.post<Array<[string, number]>>(`/api/sources/${sourceId}/vod/categories`, { ...providerBody(), kind: query.kind });
  const parental = parentalNow();
  const locked = (categories ?? []).map(([id]) => id).filter((id) => isCategoryLocked(id, parental));
  const page = await api.post<CatalogPage<T>>(`/api/sources/${sourceId}/vod/query`, { ...providerBody(), query, excludeCategories: locked });
  return page ?? { total: 0, items: [] };
}

async function catalog<T extends VodItem | SeriesItem>(sourceId: string, query: CatalogQuery): Promise<T[]> {
  return (await catalogPage<T>(sourceId, query)).items;
}

function titleEntry(item: VodItem | SeriesItem) {
  const year = "year" in item && item.year ? String(item.year) : "";
  const rating = item.rating ? `★ ${Number(item.rating).toFixed(1)}` : "";
  return { id: item.id, title: item.rawTitle, subtitle: [year, rating].filter(Boolean).join(" · "), poster: item.posterUrl ?? null };
}

async function titles(sourceId: string, kind: CatalogKind, query: Omit<CatalogQuery, "kind">) {
  if (query.ids && query.ids.length === 0) return [];
  return (await catalog(sourceId, { kind, ...query })).map(titleEntry);
}

async function answer(request: PhoneRequest, context: Context): Promise<Record<string, unknown>> {
  const { channels, sourceId } = context;
  const args = request.args ?? {};
  switch (request.op) {
    case "state":
      return withNowPlaying(context, (nowPlaying) => playingState(channels, nowPlaying));
    case "categories":
      return { categories: phoneCategories(channels) };
    case "channels": {
      const limit = useMultiviewStore.getState().limit;
      const page = await withNowPlaying(context, (nowPlaying) => phoneChannelPage(channels, args, nowPlaying));
      return { ...page, maxTiles: Math.min(limit ?? MAX_MULTIVIEW_TILES, MAX_MULTIVIEW_TILES) };
    }
    case "search": {
      const q = String(args.q ?? "").trim();
      if (q.length < 2) return { channels: [], movies: [], series: [] };
      const search = normalize(q);
      const [movies, series, found] = await Promise.all([
        titles(sourceId, "vod", { search, limit: 12 }),
        titles(sourceId, "series", { search, limit: 12 }),
        withNowPlaying(context, (nowPlaying) => phoneChannelSearch(channels, q, nowPlaying)),
      ]);
      return { channels: found, movies, series };
    }
    // The phone's Media tab: Movies and Series beside Channels (owner, 2026-10-10).
    case "titleCategories": {
      const kind: CatalogKind = args.kind === "series" ? "series" : "vod";
      const open = await openCategories(sourceId, kind);
      const counts = new Map(open);
      // The order the TV's own Movies and Series pages use.
      const { newest, rest } = orderMovieGenres(open.map(([id]) => id), viewerCountry());
      return { categories: [...newest, ...rest].map((id) => ({ id, name: genreLabel(id), count: counts.get(id) ?? 0 })) };
    }
    case "titles": {
      const kind: CatalogKind = args.kind === "series" ? "series" : "vod";
      const category = typeof args.category === "string" && args.category ? args.category : undefined;
      const text = String(args.text ?? "").trim().slice(0, 100);
      const offset = Math.max(0, Math.floor(Number(args.offset) || 0));
      const limit = Math.max(1, Math.min(100, Math.floor(Number(args.limit) || 60)));
      const page = await catalogPage(sourceId, { kind, ...(category ? { category } : {}), ...(text ? { text } : {}), sort: "pos", offset, limit });
      return { total: page.total, items: page.items.map(titleEntry) };
    }
    case "favorites": {
      const favorites = useFavoritesStore.getState();
      const byId = new Map(channels.map((channel) => [channel.id, channel]));
      const starred = favorites
        .favoritesOf("channel")
        .map((id) => byId.get(id))
        .filter((channel): channel is Channel => Boolean(channel));
      const favoriteChannels = await withNowPlaying(context, (nowPlaying) => starred.map((channel) => phoneChannel(channel, nowPlaying)));
      const [movies, series] = await Promise.all([
        titles(sourceId, "vod", { ids: favorites.favoritesOf("movie"), limit: 60 }),
        titles(sourceId, "series", { ids: favorites.favoritesOf("series"), limit: 60 }),
      ]);
      return { channels: favoriteChannels, movies, series };
    }
    case "recordings": {
      if (!dvrAvailable()) return { unavailable: true, items: [] };
      const state = await dvr.getState();
      const items = (state?.recordings ?? [])
        .filter((r) => r.status === "done" || r.status === "partial" || r.status === "recording")
        .sort((a, b) => b.startMs - a.startMs)
        .slice(0, 100)
        .map((r) => ({
          id: r.id,
          title: r.subtitle ? `${r.title} — ${r.subtitle}` : r.title,
          subtitle: [new Date(r.startMs).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }), r.channelName, r.status === "recording" ? "recording now" : ""].filter(Boolean).join(" · "),
          poster: null,
          playable: Boolean(r.fileUri),
        }));
      return { items };
    }
    case "profiles": {
      const { profiles, activeProfileId } = useProfiles.getState();
      return { items: profiles.map((p) => ({ id: p.id, name: p.name, kids: p.isKids, active: p.id === activeProfileId })) };
    }
    case "profile": {
      const id = String(args.id ?? "");
      const { profiles, activeProfileId, setActiveProfile } = useProfiles.getState();
      if (!profiles.some((p) => p.id === id)) return { ok: false, message: "That profile is gone." };
      if (id === activeProfileId) return { ok: true };
      // The phone never gets round the Kids PIN: leaving a Kids profile is done on the TV.
      if (profileSwitchNeedsPin(activeProfile(), Boolean(useParental.getState().pinHash))) {
        return { ok: false, message: "Leaving the Kids profile needs the PIN. Switch on the TV." };
      }
      setActiveProfile(id);
      return { ok: true };
    }
    case "play":
      return play(String(args.kind ?? ""), String(args.id ?? ""), context);
    case "multiview":
      return multiview(Array.isArray(args.ids) ? args.ids.map(String) : [], channels);
    default:
      return { ok: false, message: "Unknown request." };
  }
}

async function play(kind: string, id: string, context: Context): Promise<Record<string, unknown>> {
  const store = usePlayerStore.getState();
  if (kind === "channel") {
    const channel = context.channels.find((c) => c.id === id);
    if (!channel) return { ok: false, message: "That channel isn't available in this profile." };
    if (useMultiviewStore.getState().active) useMultiviewStore.getState().exit();
    void store.playSource({ url: channel.streamUrl, type: "live", title: channel.name, altUrls: channel.altStreamUrls, mirrorKey: mirrorKeyFor(context.sourceId, channel.id) });
    // Full screen, whatever the TV was showing: a pick from the phone is a pick to watch.
    usePlayerStore.getState().requestFullscreen();
    return { ok: true, message: `Playing ${channel.name}` };
  }
  if (kind === "movie") {
    const [item] = await catalog<VodItem>(context.sourceId, { kind: "vod", ids: [id], limit: 1 });
    if (!item) return { ok: false, message: "That movie isn't available in this profile." };
    // As the Movies page's Play: remembered for Continue Watching, resumed where it was left.
    const saved = useProgressStore.getState().get(item.id);
    store.setPlayingContent({ contentType: "vod", contentRef: item.id });
    useProgressStore.getState().remember(item.id, { title: item.rawTitle, posterUrl: item.posterUrl, url: item.streamUrl });
    // Other streams of this same film, tried if this one will not play (the TV app only: the phone
    // remote runs on the device, whose catalog answers).
    const alternates = await alternateStreamUrls(null, item);
    await store.playSource({
      url: item.streamUrl,
      type: "vod",
      title: item.rawTitle,
      artworkUrl: item.posterUrl,
      ...(alternates.length > 0 ? { altUrls: alternates } : {}),
    });
    // Full screen like a channel (owner, 2026-10-10); only a series needs its page.
    usePlayerStore.getState().requestFullscreen();
    if (isResumable(saved)) usePlayerStore.getState().seek(saved!.positionSeconds);
    return { ok: true, message: `Playing ${item.rawTitle}` };
  }
  if (kind === "series") {
    const [item] = await catalog<SeriesItem>(context.sourceId, { kind: "series", ids: [id], limit: 1 });
    if (!item) return { ok: false, message: "That series isn't available in this profile." };
    // A series opens its page on the TV, where its seasons and episodes are —
    // out of full screen first, or the picture stays over it (useRemoteAppActions' go).
    runRemoteAction("exitFullscreen");
    context.navigate(`/series?q=${encodeURIComponent(item.rawTitle)}&open=${encodeURIComponent(item.id)}`);
    return { ok: true, message: `Opened ${item.rawTitle} on the TV` };
  }
  if (kind === "recording") {
    const state = await dvr.getState();
    const recording = state?.recordings.find((r) => r.id === id);
    if (!recording?.fileUri) return { ok: false, message: "That recording can't be played." };
    void store.playSource({ url: recording.fileUri, type: "vod", title: recording.subtitle ? `${recording.title} — ${recording.subtitle}` : recording.title });
    usePlayerStore.getState().requestFullscreen();
    return { ok: true, message: "Playing the recording" };
  }
  return { ok: false, message: "That can't be played." };
}

/** Opens multiview with the chosen channels, up to what this TV and the provider allow. */
async function multiview(ids: string[], channels: readonly Channel[]): Promise<Record<string, unknown>> {
  const chosen = ids.map((id) => channels.find((c) => c.id === id)).filter((c): c is Channel => Boolean(c)).slice(0, MAX_MULTIVIEW_TILES);
  if (chosen.length < 2) return { ok: false, message: "Pick at least two channels." };
  const store = useMultiviewStore.getState();
  if (store.active) store.exit();
  const asTile = (c: Channel) => ({ id: c.id, name: c.name, number: c.number ?? null, logoUrl: c.logoUrl ?? null, streamUrl: c.streamUrl, altStreamUrls: c.altStreamUrls });
  await useMultiviewStore.getState().openWith(asTile(chosen[0]!));
  // The tile limit is worked out when multiview opens (MultiviewOverlay); wait for it.
  for (let i = 0; i < 40 && useMultiviewStore.getState().limit === null; i++) await new Promise((r) => setTimeout(r, 100));
  for (const channel of chosen.slice(1)) await useMultiviewStore.getState().addChannel(asTile(channel));
  const shown = useMultiviewStore.getState().tiles.length;
  return { ok: true, message: shown < chosen.length ? `Showing ${shown}: that is all this TV or provider allows.` : `Showing ${shown} channels` };
}
